import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';
import usePreventTabClose from 'src/hooks/usePreventTabClose';

export interface UploadProgress {
  totalProgress: number;
  successCount: number;
  failedCount: number;
  fileCount: number;
  droppedCount: number;
}

const defaultProgress: UploadProgress = {
  totalProgress: 0,
  successCount: 0,
  failedCount: 0,
  fileCount: 0,
  droppedCount: 0
};

/**
 * Lets a caller (e.g. a paused Upload Session's "Resume" flow with no files
 * left in memory) reuse this dialog's drag-and-drop UI to pick files, while
 * handling the result itself instead of running the normal upload pipeline.
 */
export type FilesOverrideHandler = (files: File[]) => void;

interface UploadWidgetContextType {
  isVisible: boolean;
  isMinimized: boolean;
  isPaused: boolean;
  parentId: string | null;
  isUploadFileAllowed: boolean;
  progress: UploadProgress;
  openUpload: (parentId?: string | null, isUploadFileAllowed?: boolean) => void;
  minimizeUpload: () => void;
  maximizeUpload: () => void;
  closeUpload: () => void;
  updateProgress: (progress: UploadProgress) => void;
  pauseUpload: () => void;
  resumeUpload: () => void;
  retryFailedUploads: () => void;
  registerPauseHandler: (handler: () => void) => void;
  registerResumeHandler: (handler: () => void) => void;
  registerRetryHandler: (handler: () => void) => void;
  registerClearFilesHandler: (handler: () => void) => void;
  filesOverrideHandlerRef: React.MutableRefObject<FilesOverrideHandler | null>;
  setFilesOverrideHandler: (handler: FilesOverrideHandler | null) => void;
  /** True while a files override is set - i.e. this dialog was opened by a
   * resume flow rather than a normal upload. Reactive (unlike the ref
   * above) so the dropzone's empty state can render a "Resume" CTA. */
  isResumeUpload: boolean;
}

const UploadWidgetContext = createContext<UploadWidgetContextType | undefined>(
  undefined
);

export const UploadWidgetProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const [isUploadFileAllowed, setIsUploadFileAllowed] = useState(true);
  const [progress, setProgress] = useState<UploadProgress>(defaultProgress);
  const pauseHandlerRef = useRef<(() => void) | null>(null);
  const resumeHandlerRef = useRef<(() => void) | null>(null);
  const retryHandlerRef = useRef<(() => void) | null>(null);
  const clearFilesHandlerRef = useRef<(() => void) | null>(null);
  const filesOverrideHandlerRef = useRef<FilesOverrideHandler | null>(null);
  const [isResumeUpload, setIsResumeUpload] = useState(false);

  const isUploading = progress.fileCount > 0 && progress.totalProgress < 100;
  usePreventTabClose(isUploading);

  const openUpload = useCallback((id?: string | null, uploadAllowed = true) => {
    setParentId(id ?? null);
    setIsUploadFileAllowed(uploadAllowed);
    setIsVisible(true);
    setIsMinimized(false);
    setIsPaused(false);
  }, []);

  const minimizeUpload = useCallback(() => setIsMinimized(true), []);
  const maximizeUpload = useCallback(() => setIsMinimized(false), []);

  const closeUpload = useCallback(() => {
    setIsVisible(false);
    setIsMinimized(false);
    setIsPaused(false);
    setParentId(null);
    setIsUploadFileAllowed(true);
    setProgress(defaultProgress);
    // A pending override that never got consumed (e.g. the user closed the
    // dialog before picking files) must not silently hijack a later,
    // unrelated upload opened through this same dialog.
    filesOverrideHandlerRef.current = null;
    setIsResumeUpload(false);
  }, []);

  const updateProgress = useCallback((data: UploadProgress) => {
    setProgress(data);
  }, []);

  const pauseUpload = useCallback(() => {
    setIsPaused(true);
    setIsVisible(false);
    setIsMinimized(false);
    pauseHandlerRef.current?.();
    clearFilesHandlerRef.current?.();
  }, []);

  const resumeUpload = useCallback(() => {
    setIsPaused(false);
    resumeHandlerRef.current?.();
  }, []);

  const retryFailedUploads = useCallback(() => {
    setIsMinimized(false);
    retryHandlerRef.current?.();
  }, []);

  const registerPauseHandler = useCallback((handler: () => void) => {
    pauseHandlerRef.current = handler;
  }, []);

  const registerResumeHandler = useCallback((handler: () => void) => {
    resumeHandlerRef.current = handler;
  }, []);

  const registerRetryHandler = useCallback((handler: () => void) => {
    retryHandlerRef.current = handler;
  }, []);

  const registerClearFilesHandler = useCallback((handler: () => void) => {
    clearFilesHandlerRef.current = handler;
  }, []);

  const setFilesOverrideHandler = useCallback(
    (handler: FilesOverrideHandler | null) => {
      filesOverrideHandlerRef.current = handler;
      setIsResumeUpload(handler !== null);
      if (handler !== null) {
        clearFilesHandlerRef.current?.();
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      isVisible,
      isMinimized,
      isPaused,
      parentId,
      isUploadFileAllowed,
      progress,
      openUpload,
      minimizeUpload,
      maximizeUpload,
      closeUpload,
      updateProgress,
      pauseUpload,
      resumeUpload,
      retryFailedUploads,
      registerPauseHandler,
      registerResumeHandler,
      registerRetryHandler,
      registerClearFilesHandler,
      filesOverrideHandlerRef,
      setFilesOverrideHandler,
      isResumeUpload
    }),
    [
      isVisible,
      isMinimized,
      isPaused,
      parentId,
      isUploadFileAllowed,
      progress,
      openUpload,
      minimizeUpload,
      maximizeUpload,
      closeUpload,
      updateProgress,
      pauseUpload,
      resumeUpload,
      retryFailedUploads,
      registerPauseHandler,
      registerResumeHandler,
      registerRetryHandler,
      registerClearFilesHandler,
      filesOverrideHandlerRef,
      setFilesOverrideHandler,
      isResumeUpload
    ]
  );

  return (
    <UploadWidgetContext.Provider value={value}>
      {children}
    </UploadWidgetContext.Provider>
  );
};

export const useUploadWidget = (): UploadWidgetContextType => {
  const ctx = useContext(UploadWidgetContext);
  if (!ctx)
    throw new Error('useUploadWidget must be used within UploadWidgetProvider');
  return ctx;
};
